<template>
  <div
    v-editable="blok"
    class="statistic"
    :class="flipcorner ? 'statistic--flip-corner' : ''"
  >
    <Icon :icon="blok.icon" />
    <p>{{ blok.label }}</p>
    <p ref="statElement" class="statistic__number text-3">
      {{ displayedStat }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { StatisticBlokProps } from '@/types';

// Props
const props = withDefaults(
  defineProps<{
    blok: StatisticBlokProps;
    flipcorner?: boolean;
  }>(),
  {
    flipcorner: false,
  }
);

const { data }: any = await useFetch(
  'https://app.shiftkey.com/api/v2/system/marketing_stats'
);

const totalHoursScheduled = computed(() => {
  return props.blok.show_total_hours_scheduled &&
    data.value &&
    data.value.data &&
    data.value.data.total_scheduled_hours
    ? data.value.data.total_scheduled_hours
    : '0';
});

let observer: IntersectionObserver;
const statElement = ref<HTMLParagraphElement>();
let count = 0;

const stat = computed(() => {
  return props.blok.show_total_hours_scheduled
    ? parseInt(totalHoursScheduled.value)
    : props.blok.stat
    ? parseInt(props.blok.stat)
    : 0;
});

const animatedNumber = ref(count.toLocaleString());

const displayedStat = computed(() => {
  return props.blok.disable_animation
    ? stat.value.toLocaleString()
    : animatedNumber.value;
});

const countUp = () => {
  const animationSpeed = props.blok.animation_speed
    ? parseInt(props.blok.animation_speed)
    : 20;

  const countUpInterval = setInterval(() => {
    if (count === stat.value) {
      clearInterval(countUpInterval);
    }

    animatedNumber.value = count.toLocaleString();

    count = count + Math.floor(stat.value / 100);
    if (count > stat.value) {
      count = stat.value;
    }
  }, animationSpeed);
};

const handleStatObserver = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting === true) {
        countUp();
      }
    });
  });

  if (statElement.value) {
    observer.observe(statElement.value);
  }
};

onMounted(() => {
  if (!props.blok.disable_animation) {
    handleStatObserver();
  }
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<style lang="scss">
.statistic {
  align-content: center;
  background-color: var(--white);
  border-radius: 16px 16px 16px 0;
  color: var(--dark-gray);
  display: grid;
  gap: 8px;
  grid-template-columns: 24px 1fr;
  min-height: 110px;
  padding-block: 12px;
  padding-inline: 12px;
  width: var(--statistic-width);

  @media all and #{$mq-xs-max} {
    --statistic-width: 260px;
  }

  @media all and #{$mq-xs-min} {
    --statistic-width: 276px;
  }

  @media all and #{$mq-m-min} {
    --statistic-width: 320px;

    gap: 16px 8px;
    min-height: 150px;
    padding-block: 24px;
    padding-inline: 24px;
  }

  @media all and #{$mq-l-min} {
    --statistic-width: 364px;
  }

  &--flip-corner {
    border-radius: 16px 0 16px 16px;
  }

  &__number {
    --max-font: 44;

    color: var(--bright-green);
    grid-column-start: 2;
    margin-inline-start: -4px;
  }

  .icon {
    width: 23px;
  }
}
</style>
